import { Link } from "react-router-dom";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";

const VERTICALS = [
  {
    name: "Restaurants",
    sub: "Menus, tables, takeaway",
    alt: "Restaurant dining room",
    image: "/marketing/vertical-restaurants.jpg",
  },
  {
    name: "Bakeries",
    sub: "Daily bakes, pre-orders",
    alt: "Bakery display counter",
    image: "/marketing/vertical-bakeries.jpg",
  },
  {
    name: "Florists",
    sub: "Bouquets, same-day delivery",
    alt: "Florist arranging a bouquet",
    image: "/marketing/vertical-florists.jpg",
  },
  {
    name: "Cafés & shops",
    sub: "Delis, boutiques & more",
    alt: "Independent neighborhood shop storefront",
    image: "/marketing/vertical-cafes.jpg",
  },
];

const FEATURES = [
  { icon: "✦", title: "Ready in no time", body: "Tell us about your business and we'll help build your site, photos and all, with a little help along the way." },
  { icon: "🛍", title: "Take orders directly", body: "Customers order for pickup or delivery, paid safely online. No commission taken from your sales, ever." },
  { icon: "▤", title: "Run it all from one place", body: "See orders, update your products, and manage your day from one simple dashboard." },
];

const FAQ = [
  { q: "How long does onboarding actually take?", a: "Most businesses finish the wizard in under an hour once their menu, photos, and legal documents are ready. If you're still gathering those, you can save your progress and come back any time.", tag: "Getting started" },
  { q: "What do I need before I start?", a: "Your product list or menu, a handful of photos, your business registration details, and a bank account for payouts. We'll tell you exactly which step needs what as you go.", tag: "Getting started" },
  { q: "Do you take a commission on orders?", a: "No. BuildHof charges one flat monthly price, whatever your order volume. What customers pay is what reaches your account, minus standard card processing fees.", tag: "Pricing & billing" },
  { q: "Can I switch between pickup and delivery?", a: "Yes — you can offer either, both, or neither on a per-product basis, and change this any time from your dashboard.", tag: "Orders & payments" },
  { q: "My business isn't a restaurant, bakery, or florist — can I still use BuildHof?", a: "If your business runs on choosing a product and picking it up or having it delivered, BuildHof likely fits — cafés, delis, and independent shops are all welcome.", tag: "Getting started" },
  { q: "I'm stuck on a step in onboarding — where do I get help?", a: "Every step in the wizard has a help icon that opens the relevant answer from this same FAQ. If that doesn't cover it, our team is one message away from your dashboard.", tag: "Onboarding help" },
];

const FAQ_TABS = ["All", "Getting started", "Pricing & billing", "Orders & payments", "Onboarding help"];

export function MarketingHomePage() {
  return (
    <MarketingLayout activePath="/">
      <section className="hero blob-field" data-testid="home-hero-section">
        <div className="blob" style={{ width: 420, height: 420, background: "#e8c9c2", top: -120, right: -80 }} />
        <div className="blob" style={{ width: 320, height: 320, background: "#cfe0c8", bottom: -60, left: -100 }} />
        <div className="wrap">
          <div className="hero-grid">
            <div>
              <span className="eyebrow" data-testid="home-hero-eyebrow">For restaurants, bakeries, florists &amp; local shops</span>
              <h1 className="headline" data-testid="home-hero-title">
                Get your business online and selling — <em>before the kettle boils.</em>
              </h1>
              <p className="hero-sub" data-testid="home-hero-subtitle">
                BuildHof gives your business a warm, simple website with online ordering — so customers nearby can find you and order directly. No commission, ever. Just one flat monthly price.
              </p>
              <p className="hero-note" data-testid="home-timing-disclaimer">
                *About an hour if your menu, photos, and legal documents are ready — it might take longer if you&apos;re still gathering them.
              </p>
              <div className="hero-cta" data-testid="home-hero-actions">
                <Link to="/signup" className="btn btn-primary" data-testid="home-primary-cta">Start onboarding →</Link>
                <Link to="/oak-and-ember" className="btn btn-ghost" data-testid="home-storefront-cta">View sample storefront →</Link>
              </div>
            </div>
            <div className="hero-visual" data-testid="home-hero-visual">
              <img
                src="/marketing/landing-hero.jpg"
                alt="A warm, welcoming restaurant interior"
                style={{ width: "100%", aspectRatio: "4 / 5", objectFit: "cover" }}
                data-testid="home-restaurant-image"
              />
              <div className="hero-tag">Live storefront, ready in an hour</div>
            </div>
          </div>
        </div>
      </section>

      <section className="verticals" id="verticals" data-testid="home-verticals-section">
        <div className="wrap">
          <div className="section-heading">
            <p className="kicker">Built for the shop on the corner</p>
            <h2>One platform, four kinds of local business.</h2>
            <p>Wherever the logic is &quot;pick a product, choose pickup or delivery, pay&quot; — BuildHof fits. Cafés, delis, and other neighborhood shops welcome too.</p>
          </div>
          <div className="vertical-grid">
            {VERTICALS.map((item) => (
              <div className="vertical-card" key={item.name} data-testid={`home-vertical-${item.name.toLowerCase().split(" ")[0]}`}>
                <img src={item.image} alt={item.alt} />
                <div className="overlay" />
                <div className="label">
                  <div className="name">{item.name}</div>
                  <div className="sub">{item.sub}</div>
                </div>
              </div>
            ))}
          </div>
          <p className="verticals-note">
            Don&apos;t see your kind of business? If your day-to-day is &quot;product, pickup or delivery, done&quot; — BuildHof was built with you in mind too.
          </p>
        </div>
      </section>

      <section className="features" id="how" data-testid="home-features-section">
        <div className="wrap">
          <div className="section-heading">
            <p className="kicker">How it works</p>
            <h2>Everything your storefront needs, nothing it doesn&apos;t.</h2>
          </div>
          <div className="feature-grid">
            {FEATURES.map((item) => (
              <div className="feature-card" key={item.title} data-testid={`home-feature-${item.title.toLowerCase().replaceAll(" ", "-")}`}>
                <div className="feature-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="faq" id="faq" data-testid="home-faq-section">
        <div className="wrap">
          <div className="faq-layout">
            <div className="faq-side">
              <p className="kicker">Questions &amp; help</p>
              <h2>Good to know before you start.</h2>
              <p>The same answers below also live inside your onboarding flow — so if you get stuck on a step, help is one click away without leaving the page.</p>
              <div className="kb-note">
                Mid-onboarding and stuck on a step? Open the matching question here, or use the help icon inside the wizard — both pull from the same up-to-date answers.
              </div>
            </div>
            <div className="faq-main">
              <div className="faq-tabs" data-testid="home-faq-tabs">
                {FAQ_TABS.map((tab, index) => (
                  <span key={tab} className={`faq-tab${index === 0 ? " active" : ""}`}>{tab}</span>
                ))}
              </div>
              <div className="faq-list" data-testid="home-faq-list">
                {FAQ.map((item, index) => (
                  <details key={item.q} open={index === 0}>
                    <summary>{item.q}</summary>
                    <div className="faq-answer">{item.a}</div>
                  </details>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
